import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const REMEMBER_LOGIN_STORAGE_KEY = 'mowe_admin_remember_login_v1';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, profile } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const rawValue = window.localStorage.getItem(REMEMBER_LOGIN_STORAGE_KEY);
    if (!rawValue) return;

    try {
      const parsed = JSON.parse(rawValue) as {
        email?: string;
        rememberMe?: boolean;
      };

      if (parsed.rememberMe && parsed.email) {
        setFormData({
          email: parsed.email,
          password: '',
          rememberMe: true,
        });
      }
    } catch {
      window.localStorage.removeItem(REMEMBER_LOGIN_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    const activationToken = (searchParams.get('activation_token') || searchParams.get('token') || '').trim();
    if (!activationToken) return;
    navigate(`/activar-cuenta?token=${encodeURIComponent(activationToken)}`, { replace: true });
  }, [searchParams, navigate]);

  useEffect(() => {
    if (profile) {
      navigate('/');
    }
  }, [profile, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await signIn(formData.email, formData.password);

      if (typeof window !== 'undefined') {
        if (formData.rememberMe) {
          window.localStorage.setItem(
            REMEMBER_LOGIN_STORAGE_KEY,
            JSON.stringify({
              email: formData.email.trim(),
              rememberMe: true,
            })
          );
        } else {
          window.localStorage.removeItem(REMEMBER_LOGIN_STORAGE_KEY);
        }
      }

      navigate('/');
    } catch (err: any) {
      setError(err?.message || 'Credenciales inválidas o sin permisos de administrador');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox' && !checked && typeof window !== 'undefined') {
      window.localStorage.removeItem(REMEMBER_LOGIN_STORAGE_KEY);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 flex flex-col justify-center items-center text-white p-12">
          <div className="mb-8">
            <i className="ri-dumbbell-line text-8xl"></i>
          </div>
          <h1 className="text-5xl font-bold mb-4">GymPro Admin</h1>
          <p className="text-xl text-center text-teal-50 max-w-md">Sistema de gestión integral para tu gimnasio</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <i className="ri-dumbbell-line text-6xl text-teal-600"></i>
            <h1 className="text-3xl font-bold text-gray-900 mt-2">GymPro Admin</h1>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Bienvenido</h2>
              <p className="text-gray-500 mt-2">Ingresa tus credenciales para continuar</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <i className="ri-error-warning-line text-red-600 text-xl mt-0.5"></i>
                <div className="flex-1">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Correo electrónico
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="ri-mail-line text-gray-400"></i>
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="username"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                    placeholder="admin@tugym.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="ri-lock-line text-gray-400"></i>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                    required
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  >
                    <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 hover:text-gray-600`}></i>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <span className="ml-2 text-sm text-gray-600">Recordar email</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {isLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Iniciando sesión...
                  </>
                ) : (
                  <>
                    <i className="ri-login-box-line"></i>
                    Iniciar sesión
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-700">Este acceso es solo para administradores del sistema.</p>
            </div>
          </div>

          <div className="mt-8 text-center text-sm text-gray-500">
            <p>&copy; 2026 GymPro. Todos los derechos reservados.</p>
            <p className="mt-1">
              <a
                href="/politica-privacidad"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-700 hover:text-teal-800 hover:underline font-medium"
              >
                Política de Privacidad
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
